import { FC, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  StaticPageLayout,
  Section,
  PageHero,
  Callout,
  Card,
  P,
  B,
  MailIcon,
  PhoneIcon,
  ClockIcon,
} from 'app/components/StaticPages';
import { PHONE_NUMBER, PHONE_HREF, CONTACT_EMAIL } from 'app/core/constants';

interface ContactCardProps {
  icon: ReactNode;
  label: string;
  note: string;
  children: ReactNode;
}

const ContactCard: FC<ContactCardProps> = ({ icon, label, note, children }) => (
  <Card sx={{ px: 3, py: { xs: 3.5, md: 4 } }}>
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: '50%',
        bgcolor: 'staticPages.orange',
        color: 'common.white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2,
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="eyebrow"
      sx={{ color: 'staticPages.text.light', mb: 1 }}
    >
      {label}
    </Typography>
    {children}
    <Typography
      variant="body2"
      sx={{ mt: 1.5, lineHeight: 1.6, color: 'staticPages.text.muted' }}
    >
      {note}
    </Typography>
  </Card>
);

const ContactUs = () => (
  <StaticPageLayout
    title="Contact Us | Save Up to 50% on Stroller Rentals"
    metaDescription="Contact Kingdom Strollers today and schedule a delivery to anywhere you stay. Stroller rentals can be expensive. Experience the best rated service in Orlando!"
  >
    <PageHero
      align="center"
      eyebrow="We're Here to Help"
      title="Contact Us"
      subtitle="Have a question? Reach out any time — a real person will get right back to you."
      subtitleMaxWidth={540}
    />

    <Section
      maxWidth={1040}
      innerSx={{
        pt: { xs: 5, md: 7 },
        pb: { xs: 6, md: 5 },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: { xs: 3, md: 2.5 },
        }}
      >
        <ContactCard
          icon={<MailIcon />}
          label="Email Us"
          note="Best for existing orders. We reply as quickly as possible."
        >
          <Box
            component="a"
            href={`mailto:${CONTACT_EMAIL}`}
            sx={{
              typography: 'body1',
              fontWeight: 600,
              color: 'staticPages.navy',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {CONTACT_EMAIL}
          </Box>
        </ContactCard>

        <ContactCard
          icon={<PhoneIcon />}
          label="Call Us"
          note="Speak directly with a manager — no automated menus."
        >
          <Typography
            variant="h3"
            component="a"
            href={PHONE_HREF}
            sx={{ color: 'staticPages.navy', textDecoration: 'none' }}
          >
            {PHONE_NUMBER}
          </Typography>
        </ContactCard>

        <ContactCard
          icon={<ClockIcon />}
          label="Hours of Operation"
          note="Open every day of the week."
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: 'staticPages.navy' }}
          >
            Sun – Sat
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 0.25, fontWeight: 600, color: 'staticPages.blue' }}
          >
            6am to 8pm
          </Typography>
        </ContactCard>
      </Box>

      <Callout sx={{ mt: 3 }}>
        <P sx={{ lineHeight: 1.7, color: 'staticPages.text.story' }}>
          <B navy>Sending an email?</B> Please include your order number if you
          have an existing order — this will greatly increase our ability to
          help you quickly. We do our best to return emails as quickly as
          possible.
        </P>
      </Callout>
    </Section>
  </StaticPageLayout>
);

export default ContactUs;
